import { Component, EventEmitter, Input, Output } from '@angular/core'
import { NavFavoritesService } from '@employer/app/services/nav-favorites.service'
import { EmployerNavigationService, type IEmployerNavigationItem } from '@employer/app/services/navigation.service'
import { GatedSettingsCategory } from '@employer/app/services/settings-catalog'
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs'

/**
 * "Favorites" section pinned to the top of the left-nav menu (CNCT-6764); hidden when empty.
 * Expand state is intentionally local to each surface instance (desktop rail vs mobile drawer) — which
 * is also why the guided tour's nav auto-collapse leaves it alone: it is not in the surface `openState`
 * map, so collapsing every nav group leaves Favorites expanded at the top, on its own.
 *
 * The header carries `data-tour-id="nav-favorites"` for the tour's Favorites step. Because the whole
 * section is `*ngIf`'d away when the user has starred nothing, that step simply finds no anchor and is
 * skipped — the graceful-skip path, not a special case.
 *
 * CNCT-6779 condensed the Company subtree out of the nav tree, so an id like `company-departments` no
 * longer resolves against `items`. Those ids stay valid — users have them persisted — so resolution
 * falls back to `SETTINGS_CATALOG`, which now owns those destinations. Without the fallback every
 * Company favourite would silently vanish from this section.
 */
@Component({
  selector: 'app-nav-favorites-section',
  template: `
    <ng-container *ngIf="favoriteItems$ | async as favorites">
      <div *ngIf="favorites.length" class="nav-group e11-mb-1" [ngClass]="{ active: isOpen }">
        <button
          type="button"
          id="nav-favorites-header"
          data-tour-id="nav-favorites"
          class="nav-link nav-row-btn e11-flex e11-items-center e11-gap-2 e11-w-full e11-text-left e11-h-12 e11-px-2 e11-cursor-pointer e11-bg-transparent e11-border-0 focus-visible:e11-outline focus-visible:e11-outline-2 focus-visible:e11-outline-skin-white/50"
          [attr.aria-expanded]="isOpen"
          (click)="toggleOpen()"
        >
          <span class="material-symbols-outlined md-18">star</span>
          <span class="nav-label e11-flex-1 e11-truncate">Favorites</span>
          <span class="material-symbols-outlined nav-chevron" [ngClass]="{ 'nav-chevron-open': isOpen }">expand_more</span>
        </button>
        <div class="nav-children" [ngClass]="{ 'nav-children-open': isOpen }">
          <div class="e11-pb-1.5 e11-flex e11-flex-col">
            <ng-container *ngFor="let fav of favorites">
              <a
                *ngIf="!fav.isVisible || (fav.isVisible | async)"
                [attr.id]="fav.favoriteId ? 'nav-favorite-' + fav.favoriteId : null"
                class="nav-link-child e11-flex e11-items-center e11-gap-2 e11-h-10 e11-pl-[34px] e11-pr-2 e11-cursor-pointer focus-visible:e11-outline focus-visible:e11-outline-2 focus-visible:e11-outline-skin-white/50"
                [routerLink]="fav.route"
                [queryParams]="fav.navigationExtras?.queryParams"
                [routerLinkActive]="'active'"
                [routerLinkActiveOptions]="{ paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }"
                (click)="onLeafClick(fav)"
              >
                <span class="nav-label e11-flex-1 e11-truncate">{{ fav.secondaryName ?? fav.name }}</span>
                <button
                  type="button"
                  class="nav-star-btn nav-star-active e11-flex e11-items-center e11-justify-center e11-shrink-0 e11-cursor-pointer e11-bg-transparent e11-border-0 e11-p-0"
                  aria-label="Remove from favorites"
                  (click)="onStarClick($event, fav)"
                >
                  <span class="material-symbols-outlined md-18 nav-star-filled">star</span>
                </button>
              </a>
            </ng-container>
          </div>
        </div>
      </div>
    </ng-container>
  `,
  styles: [
    `
      /* Copied verbatim from nav-item.component.ts (scoped styles) — keep the two in sync */
      .nav-label {
        font-size: 15px;
      }

      .nav-link {
        color: rgba(255, 255, 255, 0.65);
        border-radius: 8px;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .nav-link:hover {
        color: rgba(255, 255, 255, 0.85);
        background: rgba(255, 255, 255, 0.08);
      }
      .nav-link-child {
        color: rgba(255, 255, 255, 0.55);
        border-radius: 8px;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .nav-link-child:hover {
        color: rgba(255, 255, 255, 0.8);
        background: rgba(255, 255, 255, 0.08);
      }
      .nav-link-child.active {
        color: #fff;
        background: rgba(255, 255, 255, 0.1);
        font-weight: 500;
      }
      .nav-group {
        border: 1px solid transparent;
        border-radius: 8px;
        transition: border-color 0.15s ease, background 0.15s ease;
      }
      .nav-group.active {
        border-color: rgba(255, 255, 255, 0.18);
        background: rgba(255, 255, 255, 0.05);
      }
      .nav-group.active > .nav-link {
        color: #fff;
      }
      .nav-row-btn {
        font: inherit;
      }
      .nav-children {
        max-height: 0;
        overflow: hidden;
        padding-left: 4px;
        padding-right: 4px;
        transition: max-height padding-top padding-bottom 0.18s ease-in-out;
      }
      .nav-children.nav-children-open {
        max-height: 2000px;
        padding-top: 4px;
        padding-bottom: 4px;
      }
      .nav-chevron {
        font-size: 18px;
        color: rgba(255, 255, 255, 0.55);
        transition: transform 0.15s ease-in-out;
      }
      .nav-chevron.nav-chevron-open {
        transform: rotate(180deg);
      }

      .nav-star-btn {
        color: rgba(255, 255, 255, 0.55);
        border-radius: 4px;
        transition: color 0.15s ease;
      }
      .nav-star-btn:hover {
        color: #fff;
      }
      .nav-star-filled {
        font-variation-settings: 'FILL' 1;
      }

      @media (prefers-reduced-motion: reduce) {
        .nav-children,
        .nav-chevron,
        .nav-link,
        .nav-link-child,
        .nav-group,
        .nav-star-btn {
          transition: none;
        }
      }
    `,
  ],
})
export class NavFavoritesSectionComponent {
  @Output() leafNavigated = new EventEmitter<IEmployerNavigationItem>()

  isOpen = true

  private items$ = new BehaviorSubject<IEmployerNavigationItem[]>([])

  @Input() set items(value: IEmployerNavigationItem[] | null) {
    this.items$.next(value ?? [])
  }

  readonly favoriteItems$: Observable<IEmployerNavigationItem[]> = combineLatest([this.navFavorites.favorites$, this.items$]).pipe(
    map(([favoriteIds, items]) =>
      favoriteIds
        .map(id => this.findByFavoriteId(items, id) ?? this.findInScreenFavorites(id) ?? this.findInSettingsCatalog(id))
        .filter((item): item is IEmployerNavigationItem => item !== undefined)
    )
  )

  constructor(private navFavorites: NavFavoritesService, private navigation: EmployerNavigationService) {}

  toggleOpen(): void {
    this.isOpen = !this.isOpen
  }

  onLeafClick(item: IEmployerNavigationItem): void {
    item.onClick?.()
    this.leafNavigated.emit(item)
  }

  onStarClick(event: Event, item: IEmployerNavigationItem): void {
    event.preventDefault()
    event.stopPropagation()
    if (item.favoriteId) this.navFavorites.toggleFavorite(item.favoriteId)
  }

  /**
   * Destinations that are now only a tab on a section screen — Templates, Applicant Search, Talent
   * Pool and the two section Settings screens. Their `screen-header` stars write ids that match no
   * nav row and no settings category, so without this they resolved to nothing and the favourite
   * silently never appeared. The registry is a held array on the service, so the `isVisible`
   * observable handed back here is stable across emissions.
   */
  private findInScreenFavorites(favoriteId: string): IEmployerNavigationItem | undefined {
    // Optional: a navigation service without the registry must degrade to "not found" rather than
    // throw, or one missing property takes the entire Favorites list down with it.
    return this.navigation.screenFavorites?.find(item => item.favoriteId === favoriteId)
  }

  /**
   * Settings-hub destinations, shaped as nav rows. `isVisible` carries the catalog entry's own gate, so
   * a flag-gated favourite (Org Charts, say) still hides itself for users whose flag is off — the
   * renderer already respects each row's gate.
   */
  private findInSettingsCatalog(favoriteId: string): IEmployerNavigationItem | undefined {
    const category: GatedSettingsCategory | undefined = this.navigation.settingsCategories.find(cat => cat.favoriteId === favoriteId)
    if (!category) return undefined
    return {
      name: category.label,
      secondaryName: category.secondaryName,
      icon: category.icon,
      route: category.route,
      isVisible: category.isVisible$,
      favoriteId: category.favoriteId,
    } as IEmployerNavigationItem
  }

  private findByFavoriteId(items: IEmployerNavigationItem[], favoriteId: string): IEmployerNavigationItem | undefined {
    for (const item of items) {
      if (item.favoriteId === favoriteId) return item
      const match = item.children?.length ? this.findByFavoriteId(item.children, favoriteId) : undefined
      if (match) return match
    }
    return undefined
  }
}
